import { QueryKeys } from '@/enums/index';
import { INextAuthSessionModel } from '@/interfaces/authentication';
import {
  IBrandList,
  IProductDetails,
  IProductList,
  IProductListPayload,
  IProductModel,
  ISearchProduct,
  IWishListProducts,
} from '@/interfaces/product';
import {
  getBrandList,
  getCrossCategorySuggestionsList,
  getMemberExclusive,
  getPopularSearches,
  getProduct,
  getProductInfoGraphic,
  getProducts,
  getRecentlyViewedProducts,
  getRecommendedProductList,
  getWishlist,
  memberExclusiveList,
  postProductInfoGraphic,
  searchAutoComplete,
  searchProduct,
} from '@/services/product.service';
import useHeaderStore from '@/store/header';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useReactQuerySession } from './reactQuerySession';

export const useProducts = (
  pageSize: number,
  pageIndex: number,
  productPayload?: IProductListPayload,
  staleTime = 0,
  IncludeGiftVouchers: boolean = false,
) => {
  let tempQueryKey = [QueryKeys.productList] as string[];
  if (productPayload?.categoryIds?.[0]) {
    tempQueryKey.push(productPayload.categoryIds[0]);
  }

  return useQuery<IProductList>(
    tempQueryKey,
    async () => {
      return await getProducts(pageSize, pageIndex, productPayload, IncludeGiftVouchers);
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: staleTime,
    },
  );
};

export const useMemberExclusive = (pageSize: number, pageIndex: number, session?: INextAuthSessionModel | null) => {
  return useQuery<IProductList>(
    [QueryKeys.memberExclusive],
    async () => {
      const memberExclusiveData = await getMemberExclusive(pageSize, pageIndex, session);
      return memberExclusiveData;
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: false,
    },
  );
};

export const useCategoryProduct = (
  moduleSeName: string,
  filters: string,
  pageIndex: number,
  pageSize: number,
  sorting: string,
  module: string,
  enabled: boolean = true,
) => {
  const { setFilterLoading, openFilterPanel, setShowloaderOverlay } = useHeaderStore(
    ({ setFilterLoading, openFilterPanel, setShowloaderOverlay }) => ({
      setFilterLoading,
      openFilterPanel,
      setShowloaderOverlay,
    }),
  );

  let tempQueryKey = [QueryKeys.singleModuleProductList, module, moduleSeName, sorting] as string[];
  if (module === 'category') {
    if (filters !== `{"categories":["${moduleSeName}"]}`) {
      tempQueryKey.push(filters);
    }
  } else if (module === 'brand') {
    if (filters !== `{"brandSeName":["${moduleSeName}"]}`) {
      tempQueryKey.push(filters);
    }
  }

  return useQuery<ISearchProduct>(
    tempQueryKey,
    async () => {
      setFilterLoading(true);
      return await searchProduct('', filters, pageIndex, pageSize, sorting);
    },
    {
      onSuccess: (res: any) => {
        console.log('FETCHED');
        setFilterLoading(false);
        openFilterPanel(false);
        setShowloaderOverlay(false);
      },
      onError: (error: any) => {
        setFilterLoading(false);
        openFilterPanel(false);
        setShowloaderOverlay(false);
      },
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      keepPreviousData: true,
      enabled: enabled,
      // staleTime: 2 * 60 * 1000, //less than 5 minutes
    },
  );
};

export const useProduct = (paramSeName: string, enableCall: boolean = true) => {
  return useQuery<IProductDetails>(
    [paramSeName],
    async () => {
      //      console.log(enableCall, paramSeName, 'paramSeName');
      return await getProduct(paramSeName);
    },
    {
      onSuccess: (res: any) => {},
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enableCall && !!paramSeName,
    },
  );
};

export const useRecentlyViewedProducts = (arrayOfProductIds?: string[]) => {
  return useQuery<IProductModel[]>(
    [QueryKeys.recentlyViewedProducts, arrayOfProductIds],
    async () => {
      return await getRecentlyViewedProducts(arrayOfProductIds || ['']);
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!arrayOfProductIds?.length,
    },
  );
};

export const useSearchProduct = (query: string, filters: string, pageIndex: number, pageSize: number, sorting: string) => {
  const { setFilterLoading, openFilterPanel, setShowloaderOverlay } = useHeaderStore(
    ({ setFilterLoading, openFilterPanel, setShowloaderOverlay }) => ({
      setFilterLoading,
      openFilterPanel,
      setShowloaderOverlay,
    }),
  );
  let queryKeys = [QueryKeys.searchProduct, query, filters, sorting];
  //console.log(queryKeys, 'useSearchProduct');
  return useQuery<ISearchProduct>(
    queryKeys,
    async () => {
      setFilterLoading(true);
      return await searchProduct(query, filters, pageIndex, pageSize, sorting);
    },
    {
      onSuccess: (search: any) => {
        setFilterLoading(false);
        openFilterPanel(false);
        setShowloaderOverlay(false);
      },
      onError: (error: any) => {
        setFilterLoading(false);
        openFilterPanel(false);
        setShowloaderOverlay(false);
      },
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      keepPreviousData: true,
    },
  );
};

export const useMamberExclusiveProduct = (
  query: string,
  filters: string,
  pageIndex: number,
  pageSize: number,
  sorting: string,
  session?: INextAuthSessionModel | null,
) => {
  const { setFilterLoading, openFilterPanel, setShowloaderOverlay } = useHeaderStore(
    ({ setFilterLoading, openFilterPanel, setShowloaderOverlay }) => ({
      setFilterLoading,
      openFilterPanel,
      setShowloaderOverlay,
    }),
  );

  return useQuery<ISearchProduct>(
    [QueryKeys.memberExclusiveList, query, filters, sorting],
    async () => {
      setFilterLoading(true);
      return await memberExclusiveList(query, filters, pageIndex, pageSize, sorting, session);
    },
    {
      onSuccess: (search: any) => {
        setFilterLoading(false);
        openFilterPanel(false);
        setShowloaderOverlay(false);
      },
      onError: (error: any) => {
        setFilterLoading(false);
        openFilterPanel(false);
        setShowloaderOverlay(false);
      },
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      keepPreviousData: true,
    },
  );
};

export const useMemberExclusiveFilterData = (
  query: string,
  filters: string,
  pageIndex: number,
  pageSize: number,
  sorting: string,
  session?: INextAuthSessionModel | null,
) => {
  const { openFilterPanel } = useHeaderStore(({ openFilterPanel }) => ({
    openFilterPanel,
  }));

  return useQuery<ISearchProduct>(
    [QueryKeys.memberExclusiveList, query, filters, sorting],
    async () => {
      return await memberExclusiveList(query, '', pageIndex, pageSize, sorting, session);
    },
    {
      onSuccess: (search: any) => {
        openFilterPanel(false);
      },
      onError: (error: any) => {
        openFilterPanel(false);
      },
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      keepPreviousData: true,
    },
  );
};

export const useFilterData = (query: string, filters: string, pageIndex: number, pageSize: number, sorting: string) => {
  const { openFilterPanel } = useHeaderStore(({ openFilterPanel }) => ({
    openFilterPanel,
  }));
  let queryKeys = [QueryKeys.searchProduct, query, filters, sorting];
  //console.log(queryKeys, 'useFilterData');
  return useQuery<ISearchProduct>(
    [QueryKeys.searchProduct, query, filters, sorting],
    async () => {
      return await searchProduct(query, '', pageIndex, pageSize, sorting);
    },
    {
      onSuccess: (search: any) => {
        openFilterPanel(false);
      },
      onError: (error: any) => {
        openFilterPanel(false);
      },
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      keepPreviousData: true,
    },
  );
};

export const useCategoryBrandFilterData = (
  moduleSeName: string,
  filters: string,
  pageIndex: number,
  pageSize: number,
  sorting: string,
  module: string,
) => {
  const { openFilterPanel } = useHeaderStore(({ openFilterPanel }) => ({
    openFilterPanel,
  }));

  let tempQueryKey = [QueryKeys.singleModuleProductList, module, moduleSeName, sorting] as string[];
  // this will be necessary if permanent filters are to be synced with products on server
  // if (module === 'category') {
  //   if (filters !== `{"categories":["${moduleSeName}"]}`) {
  //     tempQueryKey.push(filters);
  //   }
  // } else if (module === 'brand') {
  //   if (filters !== `{"brandSeName":["${moduleSeName}"]}`) {
  //     tempQueryKey.push(filters);
  //   }
  // }
  return useQuery<ISearchProduct>(
    tempQueryKey,
    async () => {
      return await searchProduct('', filters, pageIndex, pageSize, sorting);
    },
    {
      onSuccess: (res: any) => {
        openFilterPanel(false);
      },
      onError: (error: any) => {
        openFilterPanel(false);
      },
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      keepPreviousData: false,
      enabled: false,
    },
  );
};

export const usePopularSearch = (searchPanel: boolean) => {
  return useQuery<any>(
    [QueryKeys.popularSearch],
    async () => {
      return await getPopularSearches();
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      enabled: searchPanel,
    },
  );
};

export const useProductInfoGraphic = (paramSeName: string) => {
  return useQuery<number>(
    [QueryKeys.getInfoGraphic + paramSeName],
    async () => {
      return await getProductInfoGraphic(paramSeName);
    },
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
};
export const useUpdateInfoGraphic = (paramSeName: string, quickView?: boolean) => {
  const queryClient = useQueryClient();
  return useMutation(postProductInfoGraphic, {
    onSuccess: () => {
      if (quickView) queryClient.invalidateQueries([QueryKeys.getInfoGraphic + paramSeName]);
    },
  });
};
export const useGetBrandList = () => {
  return useQuery<IBrandList[]>(
    [QueryKeys.brandList],
    async () => {
      return await getBrandList();
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
};

export const useSearchAutoComplete = (query: string, searchPanel: boolean) => {
  return useQuery<ISearchProduct>(
    [QueryKeys.autoComplete, query],
    async () => {
      let autoCompleteData = await searchAutoComplete(query);
      return autoCompleteData;
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: searchPanel,
    },
  );
};
export const useRecommendedProduct = (paramSeName: string) => {
  return useQuery<IProductModel[]>(
    [QueryKeys.recommendedProducts + paramSeName],
    async () => {
      return await getRecommendedProductList(paramSeName);
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!paramSeName,
    },
  );
};
export const useCrossCategoryProduct = (paramSeName: string) => {
  return useQuery<IProductModel[]>(
    [QueryKeys.crossCategorySuggestions + paramSeName],
    async () => {
      return await getCrossCategorySuggestionsList(paramSeName);
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!paramSeName,
    },
  );
};
export const useWishlistProducts = (paramSeName?: string, session?: INextAuthSessionModel | null, isAuthenticated?: boolean) => {
  return useQuery<IWishListProducts>(
    [QueryKeys.wishlist + paramSeName],
    async () => {
      return await getWishlist(paramSeName as string, session);
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!paramSeName && isAuthenticated,
    },
  );
};
